<template>
  <modal id="customersForm"
         v-model="visible"
         :title="id ? $t('a-customersForm:updateCustomer') : $t('a-customersForm:createCustomer')"
         no-footer
         @modal:cancel="cancel"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('a-customersForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <stepper ref="stepper" v-model="step" :steps="steps" :on-next="next" />

      <template v-if="step === 0">
        <div class="form-group">
          <label for="name" class="control-label">{{ $t('a-customersForm:name') | capitalize }} *</label>
          <input id="name"
                 key="name"
                 v-model="name"
                 v-validate="'required|text'"
                 :data-vv-as="$t('a-customersForm:name')"
                 :class="{ 'is-invalid': errors.has('name') }"
                 name="name"
                 type="text"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
        </div>

        <div class="form-group">
          <label for="logo" class="control-label">{{ $t('a-customersForm:logo') | capitalize }}</label>
          <input id="logo"
                 key="logo"
                 v-model="logo"
                 v-validate="'url'"
                 :data-vv-as="$t('a-customersForm:logo')"
                 :class="{ 'is-invalid': errors.has('logo') }"
                 name="logo"
                 type="text"
                 class="form-control"
          >
          <small class="form-text text-muted mt-2">{{ $t('a-customersForm:logoComment') | capitalize }}</small>
          <div class="invalid-feedback">{{ errors.first('logo') }}</div>
        </div>

        <div class="form-row">
          <div class="form-group col-sm-12">
            <label class="control-label d-block">{{ $t('a-customersForm:billing') | capitalize }} *</label>
            <input id="billingEnabled"
                   v-model="billing"
                   v-validate="'required'"
                   :data-vv-as="$t('a-customersForm:billing')"
                   :class="{ 'is-invalid': errors.has('billing') }"
                   :value="true"
                   type="radio"
                   name="billing"
            >
            <label for="billingEnabled" class="control-label">{{ $t('a-customersForm:billingEnabled') | capitalize }}</label>

            <input id="billingDisabled"
                   v-model="billing"
                   :class="{ 'is-invalid': errors.has('billing') }"
                   :value="false"
                   type="radio"
                   name="billing"
            >
            <label for="billingDisabled" class="control-label">{{ $t('a-customersForm:billingDisabled') | capitalize }}</label>

            <div class="invalid-feedback">{{ errors.first('billing') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 1">
        <h3 class="mt-3">{{ $t('a-customersForm:limits') | capitalize }}</h3>
        <p class="text-muted font-italic">{{ $t('a-customersForm:limitsHelper') | capitalize }}</p>

        <div class="form-row">
          <div class="form-group col-sm-4">
            <label for="maxGateways" class="control-label">{{ $t('a-customersForm:maxGateways') | capitalize }}</label>
            <input id="maxGateways"
                   key="maxGateways"
                   v-model="maxGateways"
                   v-validate="'number|min_value:1'"
                   :data-vv-as="$t('a-customersForm:maxGateways')"
                   :class="{ 'is-invalid': errors.has('maxGateways') }"
                   name="maxGateways"
                   type="text"
                   class="form-control"
                   min="1"
            >
            <div class="invalid-feedback">{{ errors.first('maxGateways') }}</div>
          </div>

          <div class="form-group col-sm-4">
            <label for="maxEndDevices" class="control-label">{{ $t('a-customersForm:maxEndDevices') | capitalize }}</label>
            <input id="maxEndDevices"
                   key="maxEndDevices"
                   v-model="maxEndDevices"
                   v-validate="'number|min_value:1'"
                   :data-vv-as="$t('a-customersForm:maxEndDevices')"
                   :class="{ 'is-invalid': errors.has('maxEndDevices') }"
                   name="maxEndDevices"
                   type="text"
                   class="form-control"
                   min="1"
            >
            <div class="invalid-feedback">{{ errors.first('maxEndDevices') }}</div>
          </div>

          <div class="form-group col-sm-4">
            <label for="maxUsers" class="control-label">{{ $t('a-customersForm:maxUsers') | capitalize }}</label>
            <input id="maxUsers"
                   key="maxUsers"
                   v-model="maxUsers"
                   v-validate="'number|min_value:1'"
                   :data-vv-as="$t('a-customersForm:maxUsers')"
                   :class="{ 'is-invalid': errors.has('maxUsers') }"
                   name="maxUsers"
                   type="text"
                   class="form-control"
                   min="1"
            >
            <div class="invalid-feedback">{{ errors.first('maxUsers') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 2">
        <h3 class="mt-3">{{ $t('a-customersForm:geolocation') | capitalize }}</h3>

        <div class="form-group">
          <input id="geolocationAuthorized"
                 v-model="geolocationAuthorized"
                 type="checkbox"
                 name="geolocationAuthorized"
          >
          <label for="geolocationAuthorized" />
          <label for="geolocationAuthorized" class="control-label">{{ $t('a-customersForm:geolocationAuthorized') | capitalize }}</label>
        </div>

        <div class="form-group">
          <label for="geolocationExpirationDate">{{ $t('a-customersForm:geolocationExpirationDate') | capitalize }}</label>
          <div class="input-group">
            <flat-pickr id="geolocationExpirationDate"
                        key="geolocationExpirationDate"
                        v-model="geolocationExpirationDate"
                        :config="fpConfig"
                        class="form-control"
            />
            <div class="input-group-append">
              <button id="resetDateButton" class="btn btn-flat btn-sm" type="button" @click="geolocationExpirationDate = null"><i class="icon-remove" /></button>
            </div>
          </div>
          <div v-if="geolocationExpirationDate && geolocationExpirationDate * 1000 < Date.now()" class="text-danger">{{ $t('a-customersForm:geolocationExpired') | capitalize }} {{ geolocationExpirationDate | date('date') }}</div>
        </div>
      </template>
    </div>

    <div slot="modal-footer" class="w-100 text-right">
      <button v-if="step > 0" id="stepperPrevious" class="btn btn-flat float-left" @click="$refs.stepper.previous()">{{ $t('stepper:previous') | capitalize }}</button>
      <button id="stepperCancel" class="btn btn-flat" @click="cancel">{{ $t('stepper:cancel') | capitalize }}</button>
      <button v-if="step < steps.length-1" id="stepperNext" class="btn btn-flat" @click="$refs.stepper.next()">{{ $t('stepper:next') | capitalize }}</button>
      <button v-if="step === steps.length-1" id="stepperValidate" class="btn btn-flat" @click="id ? updateCustomer() : createCustomer()">{{ $t('stepper:validate') | capitalize }}</button>
    </div>
  </modal>
</template>

<script>
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import { pick } from 'lodash-es'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    customerToEdit: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      id: '',
      name: '',
      logo: '',
      billing: false,
      maxGateways: null,
      maxEndDevices: null,
      maxUsers: null,
      geolocationAuthorized: false,
      geolocationExpirationDate: null,
      // Stepper
      step: 0,
      steps: [
        { label: this.$t('a-customersForm:identity') },
        { label: this.$t('a-customersForm:limitations') },
        { label: this.$t('a-customersForm:geolocation') }
      ],

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        altInput: true,
        altFormat: 'Y/m/d',
        dateFormat: 'U',
        minDate: Date.now() + 1000 * 60 * 60 * 24
      }
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show
    },
    visible () {
      this.$emit('change', this.visible)
    },

    customerToEdit () {
      if (this.customerToEdit) {
        this.id = this.customerToEdit.id
        this.name = this.customerToEdit.name
        this.logo = this.customerToEdit.logo
        this.billing = this.customerToEdit.billing
        this.maxGateways = this.customerToEdit.maxGateways
        this.maxEndDevices = this.customerToEdit.maxEndDevices
        this.maxUsers = this.customerToEdit.maxUsers
        this.geolocationAuthorized = this.customerToEdit.geolocationAuthorized
        // Need to set date in second and as a string to prevent a big mess up between get and set
        this.geolocationExpirationDate = this.customerToEdit.geolocationExpirationDate ? '' + this.customerToEdit.geolocationExpirationDate / 1000 : null
      }
    }
  },

  methods: {
    async reset () {
      this.id = ''
      this.name = ''
      this.logo = ''
      this.billing = false
      this.maxGateways = null
      this.maxEndDevices = null
      this.maxUsers = null
      this.geolocationAuthorized = false
      this.geolocationExpirationDate = null
      this.step = 0

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    cancel () {
      this.visible = false
      this.step = 0
      this.$emit('cancel')
    },

    async next () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      return true
    },

    async createCustomer () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const fields = [
        'name', 'logo', 'billing',
        'maxGateways', 'maxEndDevices', 'maxUsers',
        'geolocationAuthorized'
      ]
      const customer = pick(this, fields)
      customer.geolocationExpirationDate = this.geolocationExpirationDate ? this.geolocationExpirationDate * 1000 : null

      let error
      [error] = await to(this.$api.createCustomer(this.$store.getters.application, customer))
      if (error) return

      this.visible = false
      this.step = 0
      // Success
      eventBus.$emit('message-success', this.$t('a-customersForm:createSuccess', { customer: this.name }))
      // Refresh list
      this.$emit('success')
      // Reset form
      this.reset()
    },

    async updateCustomer () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const fields = [
        'name', 'logo', 'billing',
        'maxGateways', 'maxEndDevices', 'maxUsers',
        'geolocationAuthorized'
      ]
      const customer = pick(this, fields)
      customer.geolocationExpirationDate = this.geolocationExpirationDate ? this.geolocationExpirationDate * 1000 : null

      let error
      [error] = await to(this.$api.patchCustomer(this.customerToEdit, customer))
      if (error) return

      this.visible = false
      this.step = 0
      // Success
      eventBus.$emit('message-success', this.$t('a-customersForm:updateSuccess', { customer: this.name }))
      // Refresh list
      this.$emit('success')
      // Reset form
      this.reset()
    }
  }
}
</script>
